import { useState, VFC } from "react";

import { Tab, Tabs } from "@portex-pro/ui-components";
import { useGetPartnersRequestQuery } from "api/rest/partners/partnersApi";
import { ShipperQuoteRequest, useUpdateShipperQuoteRequestMutation } from "api/rest/quote-requests";
import EmailPreviewSection from "app/pages/quote-request/_components/EmailPreviewSection";
import PartnerTable from "components/partners/PartnerTable";
import PortexDialog from "components/PortexDialog";
import SimpleSearchView from "components/SimpleSearchView";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Sentry } from "sentry";

type ResendQuoteRequestDialogProps = {
  quoteRequest: ShipperQuoteRequest;
  open: boolean;
  onClose: () => void;
};

const ResendQuoteRequestDialog: VFC<ResendQuoteRequestDialogProps> = (props) => {
  const { quoteRequest, open, onClose } = props;
  const { t } = useTranslation(["common", "shipper"]);
  const { enqueueSnackbar } = useSnackbar();

  const [selectedPartnerIds, setSelectedPartnerIds] = useState(
    quoteRequest.partners.map((partner) => partner.contact_id.toString())
  );

  const { data: newPartnerData } = useGetPartnersRequestQuery({
    queryParams: { ids: selectedPartnerIds.map((id) => Number(id)) },
  });

  const [updateQuoteRequest, { isLoading }] = useUpdateShipperQuoteRequestMutation();

  const resendEmails = async () => {
    if (!selectedPartnerIds.length) {
      return;
    }

    try {
      await updateQuoteRequest({
        urlParams: { quoteRequestId: quoteRequest.id },
        body: {
          resendTo: selectedPartnerIds.map((id) => ({ contactId: Number(id) })),
        },
      }).unwrap();

      enqueueSnackbar(t("shipper:resendQuoteRequest_success"), { variant: "success" });
    } catch (e) {
      Sentry.captureException(e);
      enqueueSnackbar(t("common:errors.generic"), { variant: "error" });
    } finally {
      onClose();
    }
  };

  const [search, setSearch] = useState<string>("");
  const [currentTab, setCurrentTab] = useState<"PREVIEW" | "PARTNERS">("PARTNERS");

  return (
    <PortexDialog
      title={t("shipper:resendQuoteRequest")}
      confirmButtonCopy={t("shipper:resendViaPortex")}
      open={open}
      onClose={onClose}
      maxWidth="md"
      onClickConfirm={resendEmails}
      confirmButtonProps={{ disabled: isLoading || !selectedPartnerIds.length }}
    >
      <div className="flex flex-col pt-2 flex flex-col">
        <div className="flex pb-4">
          <Tabs value={currentTab}>
            <Tab label={t("shipper:selectPartners")} value={"PARTNERS"} onClick={() => setCurrentTab("PARTNERS")} />
            <Tab label={t("shipper:previewQuote")} value={"PREVIEW"} onClick={() => setCurrentTab("PREVIEW")} />
          </Tabs>
        </div>

        {currentTab === "PREVIEW" && (
          <EmailPreviewSection
            quoteRequest={
              !!selectedPartnerIds.length
                ? !!newPartnerData
                  ? {
                      ...quoteRequest,
                      partners: newPartnerData.data.partners.map((partner) => ({
                        contact_id: partner.id,
                        company_name: partner.company_name,
                        email: partner.user.email,
                      })) as ShipperQuoteRequest["partners"],
                    }
                  : quoteRequest
                : { ...quoteRequest, partners: [] as ShipperQuoteRequest["partners"] }
            }
          />
        )}

        {currentTab === "PARTNERS" && (
          <PartnerTable
            selectedPartnerIds={new Set(selectedPartnerIds)}
            showPagination
            tableHeight="680px"
            tableClassName="min-h-[680px]"
            searchValue={search}
            headerSlot={
              <div className="px-4 mb-4">
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  <b>{t("shipper:allPartnersBCCd")}</b>
                </div>
              </div>
            }
            searchSlot={
              <SimpleSearchView
                search={search ?? ""}
                onChange={(search) => setSearch(search)}
                placeholder={t("shipper:searchPartners")}
              />
            }
            onSelectionChange={(partnerIds) => setSelectedPartnerIds(Array.from(partnerIds).map((id) => id.toString()))}
          />
        )}
      </div>
    </PortexDialog>
  );
};

export default ResendQuoteRequestDialog;
